<script setup lang="ts">
import convertAzureIcon from '@/utils/azure-icons'
import { notifyError } from '@/utils/helpers'

interface WeatherValue {
    value: number
    unit: string
}

interface HourlyForecast {
    date: string
    iconCode: number
    isDaylight: boolean
    temperature: WeatherValue
    precipitationProbability: number
    totalLiquid: WeatherValue
    wind?: { speed: WeatherValue }
    relativeHumidity?: number
}

interface DailyForecast {
    date: string
    temperature: { maximum: WeatherValue; minimum: WeatherValue }
    day: {
        iconCode: number
        shortPhrase: string
        precipitationProbability: number
        totalLiquid: WeatherValue
    }
}

const theme = useTheme()
const storeU = useUtilisateurStore()
const { isAuthenticated, isPending } = useKeycloak()

const hourly = ref<HourlyForecast[]>([])
const daily = ref<DailyForecast[]>([])
const isLoading = ref(true)

// Apple-style hourly strip (next 24 h)
const hourlyStrip = computed(() =>
    hourly.value.map((f, i) => {
        const d = new Date(f.date)
        return {
            label: i === 0 ? 'Maint.' : d.getHours() + ' h',
            icon: convertAzureIcon(f.iconCode, f.isDaylight ?? true),
            prob: f.precipitationProbability,
            temp: Math.round(f.temperature.value),
        }
    }),
)

type SparkType = 'trend' | 'bar'

interface Metric {
    title: string
    icon: string
    data: number[]
    unit: string
    color: string
    type: SparkType
    fill: boolean
    precision: number
}

const windUnit = computed(() => hourly.value[0]?.wind?.speed.unit ?? 'km/h')

// Each hourly metric becomes its own minimalistic sparkline widget
const metrics = computed<Metric[]>(() => {
    if (!hourly.value.length) return []

    const list: Metric[] = [
        {
            title: 'Température',
            icon: 'M15 13V5A3 3 0 0 0 9 5V13A5 5 0 1 0 15 13M12 4A1 1 0 0 1 13 5V8H11V5A1 1 0 0 1 12 4Z',
            data: hourly.value.map((f) => Math.round(f.temperature.value)),
            unit: '°',
            color: '#ef6c00',
            type: 'trend',
            fill: true,
            precision: 0,
        },
        {
            title: 'Probabilité de pluie',
            icon: 'M12,3.25C12,3.25 6,10 6,14C6,17.32 8.69,20 12,20A6,6 0 0,0 18,14C18,10 12,3.25 12,3.25M14.47,9.97L15.53,11.03L9.53,17.03L8.47,15.97M9.75,10A1.25,1.25 0 0,1 11,11.25A1.25,1.25 0 0,1 9.75,12.5A1.25,1.25 0 0,1 8.5,11.25A1.25,1.25 0 0,1 9.75,10M14.25,14.5A1.25,1.25 0 0,1 15.5,15.75A1.25,1.25 0 0,1 14.25,17A1.25,1.25 0 0,1 13,15.75A1.25,1.25 0 0,1 14.25,14.5Z',
            data: hourly.value.map((f) => f.precipitationProbability),
            unit: ' %',
            color: '#3ba0ff',
            type: 'trend',
            fill: true,
            precision: 0,
        },
        {
            title: 'Quantité de pluie',
            icon: 'M9,12C9.53,12.14 9.85,12.69 9.71,13.22L8.41,18.05C8.27,18.59 7.72,18.9 7.19,18.76C6.65,18.62 6.34,18.07 6.5,17.54L7.78,12.71C7.92,12.17 8.47,11.86 9,12M13,12C13.53,12.14 13.85,12.69 13.71,13.22L11.64,20.95C11.5,21.5 10.95,21.8 10.41,21.66C9.88,21.5 9.56,20.97 9.7,20.43L11.78,12.71C11.92,12.17 12.47,11.86 13,12M17,12C17.53,12.14 17.85,12.69 17.71,13.22L16.41,18.05C16.27,18.59 15.72,18.9 15.19,18.76C14.65,18.62 14.34,18.07 14.5,17.54L15.78,12.71C15.92,12.17 16.47,11.86 17,12M17,10V9A5,5 0 0,0 12,4C9.5,4 7.45,5.82 7.06,8.19C6.73,8.07 6.37,8 6,8A3,3 0 0,0 3,11C3,12.11 3.6,13.08 4.5,13.6V13.59C5,13.87 5.14,14.5 4.87,14.96C4.59,15.43 4,15.6 3.5,15.32V15.33C2,14.47 1,12.85 1,11A5,5 0 0,1 6,6C7,3.65 9.3,2 12,2C15.43,2 18.24,4.66 18.5,8.03L19,8A4,4 0 0,1 23,12C23,13.5 22.2,14.77 21,15.46V15.46C20.5,15.73 19.91,15.57 19.63,15.09C19.36,14.61 19.5,14 20,13.72V13.73C20.6,13.39 21,12.74 21,12A2,2 0 0,0 19,10H17Z',
            data: hourly.value.map((f) => Math.round(f.totalLiquid.value * 10) / 10),
            unit: ' mm',
            color: '#2979ff',
            type: 'bar',
            fill: false,
            precision: 1,
        },
    ]

    if (hourly.value.some((f) => f.wind)) {
        list.push({
            title: 'Vent',
            icon: 'M4,10A1,1 0 0,1 3,9A1,1 0 0,1 4,8H12A2,2 0 0,0 14,6A2,2 0 0,0 12,4C11.45,4 10.95,4.22 10.59,4.59C10.2,5 9.56,5 9.17,4.59C8.78,4.2 8.78,3.56 9.17,3.17C9.9,2.45 10.9,2 12,2A4,4 0 0,1 16,6A4,4 0 0,1 12,10H4M19,12A1,1 0 0,0 20,11A1,1 0 0,0 19,10C18.72,10 18.47,10.11 18.29,10.29C17.9,10.68 17.27,10.68 16.88,10.29C16.5,9.9 16.5,9.27 16.88,8.88C17.42,8.34 18.17,8 19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14H5A1,1 0 0,1 4,13A1,1 0 0,1 5,12H19M18,18H4A1,1 0 0,1 3,17A1,1 0 0,1 4,16H18A3,3 0 0,1 21,19A3,3 0 0,1 18,22C17.17,22 16.42,21.66 15.88,21.12C15.5,20.73 15.5,20.1 15.88,19.71C16.27,19.32 16.9,19.32 17.29,19.71C17.47,19.89 17.72,20 18,20A1,1 0 0,0 19,19A1,1 0 0,0 18,18Z',
            data: hourly.value.map((f) => Math.round(f.wind?.speed.value ?? 0)),
            unit: ` ${windUnit.value}`,
            color: '#00897b',
            type: 'trend',
            fill: true,
            precision: 0,
        })
    }

    if (hourly.value.some((f) => f.relativeHumidity != null)) {
        list.push({
            title: 'Humidité',
            icon: 'M12,20A6,6 0 0,1 6,14C6,10 12,3.25 12,3.25C12,3.25 18,10 18,14A6,6 0 0,1 12,20Z',
            data: hourly.value.map((f) => f.relativeHumidity ?? 0),
            unit: ' %',
            color: '#26c6da',
            type: 'trend',
            fill: true,
            precision: 0,
        })
    }

    return list
})

const formatDay = (dateString: string) => {
    const date = new Date(dateString)
    return {
        weekday: date.toLocaleDateString('fr-CA', { weekday: 'short' }),
        date: date.toLocaleDateString('fr-CA', { day: 'numeric', month: 'short' }),
    }
}

const isToday = (dateString: string) => {
    const d = new Date(dateString)
    const now = new Date()
    return d.toDateString() === now.toDateString()
}

// Map a temperature (°C) to a hue: cold = blue, hot = red
const tempColor = (t: number) => {
    const hue = Math.max(0, Math.min(220, 220 - ((t + 10) / 45) * 220))
    return `hsl(${hue}, 75%, 55%)`
}

// Daily rows with Low→High range bars aligned to a shared temperature scale
const dailyRows = computed(() => {
    if (!daily.value.length) return []

    const mins = daily.value.map((d) => d.temperature.minimum.value)
    const maxs = daily.value.map((d) => d.temperature.maximum.value)
    const gMin = Math.min(...mins)
    const gMax = Math.max(...maxs)
    const span = gMax - gMin || 1
    const padding = Math.max(span * 0.15, 1)
    const gMinP = gMin - padding
    const spanP = span + padding * 2

    return daily.value.map((d) => {
        const lo = d.temperature.minimum.value
        const hi = d.temperature.maximum.value
        const left = ((lo - gMinP) / spanP) * 100
        const width = Math.max(((hi - lo) / spanP) * 100, 8)
        return {
            key: d.date,
            today: isToday(d.date),
            weekday: isToday(d.date) ? 'Auj.' : formatDay(d.date).weekday,
            date: formatDay(d.date).date,
            icon: convertAzureIcon(d.day.iconCode, true),
            prob: d.day.precipitationProbability,
            lo: Math.round(lo),
            hi: Math.round(hi),
            barStyle: {
                left: `${left}%`,
                width: `${width}%`,
                background: `linear-gradient(90deg, ${tempColor(lo)}, ${tempColor(hi)})`,
            },
        }
    })
})

async function loadHourly() {
    const site = storeU.siteSélectionné
    if (!site?.latitudeGoogleMaps) return
    try {
        const config = {
            params: {
                latitude: site.latitudeGoogleMaps,
                longitude: site.longitudeGoogleMaps,
                forecastHours: 24,
            },
        }
        const response = await apiOmni.get('api/azuremaps/weather/forecast/hourly', config)
        if (Array.isArray(response.data.forecasts)) {
            hourly.value = response.data.forecasts
        }
    } catch (error) {
        console.error('Error loading hourly weather:', error)
        notifyError(error)
    }
}

async function loadDaily() {
    const site = storeU.siteSélectionné
    if (!site?.latitudeGoogleMaps) return
    try {
        const config = {
            params: {
                latitude: site.latitudeGoogleMaps,
                longitude: site.longitudeGoogleMaps,
                forecastDays: 5,
            },
        }
        const response = await apiOmni.get('api/azuremaps/weather/forecast/daily', config)
        if (Array.isArray(response.data.forecasts)) {
            daily.value = response.data.forecasts
        }
    } catch (error) {
        console.error('Error loading daily weather:', error)
        notifyError(error)
    }
}

async function loadAll() {
    isLoading.value = true
    try {
        await Promise.all([loadHourly(), loadDaily()])
    } finally {
        isLoading.value = false
    }
}

watch(() => storeU.siteSélectionné, (newSite) => {
    if (newSite) loadAll()
})

onMounted(async () => {
    while (isPending.value || !isAuthenticated.value) {
        await new Promise((resolve) => setTimeout(resolve, 100))
    }
    if (storeU.siteSélectionné) await loadAll()
})
</script>


<template>
    <div class="forecasts d-flex flex-column" style="gap: 16px;">
        <!-- Widget · hourly strip -->
        <v-card class="widget" rounded="xl" elevation="0" :loading="isLoading">
            <div class="widget-head">
                <v-icon icon="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z" size="18" class="widget-icon" />
                <span class="widget-title">Prévisions horaires</span>
                <span class="widget-sub">Prochaines 24 h</span>
            </div>
            <div v-if="isLoading && !hourlyStrip.length" class="px-4 pb-4">
                <v-skeleton-loader type="image" height="90" />
            </div>
            <div v-else-if="hourlyStrip.length" class="hourly-strip">
                <div v-for="(h, i) in hourlyStrip" :key="i" class="hour-cell">
                    <div class="hour-label">{{ h.label }}</div>
                    <v-icon :icon="h.icon" size="26" class="hour-icon" />
                    <div class="hour-prob" :class="{ 'hour-prob--zero': h.prob === 0 }">{{ h.prob }}%</div>
                    <div class="hour-temp">{{ h.temp }}°</div>
                </div>
            </div>
            <div v-else class="pa-6 text-center text-medium-emphasis">Aucune donnée horaire</div>
        </v-card>

        <!-- Dashboard · 24 h trends + 5-day forecast -->
        <v-row dense>
            <!-- Sparkline metrics -->
            <v-col cols="12" lg="7">
                <v-row dense>
                    <template v-if="isLoading && !metrics.length">
                        <v-col v-for="n in 4" :key="`s-${n}`" cols="6" sm="4" lg="6">
                            <v-skeleton-loader type="image" height="150" class="rounded-xl" />
                        </v-col>
                    </template>
                    <v-col v-for="m in metrics" :key="m.title" cols="6" sm="4" lg="6">
                        <MetricSparkline v-bind="m" />
                    </v-col>
                </v-row>
            </v-col>

            <!-- 5-day forecast -->
            <v-col cols="12" lg="5">
                <v-card class="widget widget--bare" rounded="xl" elevation="0" :loading="isLoading">
                    <div class="widget-head">
                        <v-icon icon="M6 1H8V3H16V1H18V3H19C20.11 3 21 3.9 21 5V19C21 20.11 20.11 21 19 21H5C3.89 21 3 20.1 3 19V5C3 3.89 3.89 3 5 3H6V1M5 8V19H19V8H5M7 10H17V12H7V10Z" size="18" class="widget-icon" />
                        <span class="widget-title">Prévisions 5 jours</span>
                    </div>
                    <div v-if="isLoading && !daily.length" class="px-4 pb-4 d-flex flex-column" style="gap: 10px;">
                        <v-skeleton-loader v-for="n in 5" :key="n" type="list-item" />
                    </div>
                    <div v-else class="forecast-list">
                        <div
                            v-for="row in dailyRows"
                            :key="row.key"
                            class="day-row"
                            :class="{ 'day-row--today': row.today }"
                        >
                            <div class="day-name">
                                <span class="day-weekday text-capitalize">{{ row.weekday }}</span>
                                <span class="day-date">{{ row.date }}</span>
                            </div>
                            <v-icon :icon="row.icon" size="24" class="day-icon" />
                            <span class="day-prob" :class="{ 'day-prob--zero': row.prob === 0 }">{{ row.prob }}%</span>
                            <span class="day-lo">{{ row.lo }}°</span>
                            <div class="range-track">
                                <div class="range-fill" :style="row.barStyle"></div>
                            </div>
                            <span class="day-hi">{{ row.hi }}°</span>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<style scoped>
.widget {
    overflow: hidden;
    background: v-bind('theme.global.name.value === "dark" ? "rgba(30, 38, 56, 0.55)" : "rgba(255, 255, 255, 0.8)"') !important;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid v-bind('theme.global.name.value === "dark" ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.5)"');
}

.widget--bare {
    background: transparent !important;
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: none;
    color: #fff;
}

.widget-head {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 18px 8px;
}

.widget-icon {
    color: rgb(var(--v-theme-primary));
}

.widget-title {
    font-size: 1rem;
    font-weight: 700;
}

.widget-sub {
    margin-left: auto;
    font-size: 0.76rem;
    opacity: 0.6;
}

.hourly-strip {
    display: flex;
    gap: 4px;
    overflow-x: auto;
    padding: 4px 14px 16px;
}

.hour-cell {
    flex: 0 0 auto;
    width: 58px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
}

.hour-label {
    font-size: 0.75rem;
    font-weight: 600;
    opacity: 0.7;
}

.hour-icon {
    color: rgb(var(--v-theme-primary));
}

.hour-prob {
    font-size: 0.72rem;
    font-weight: 600;
    color: #3ba0ff;
}

.hour-prob--zero {
    opacity: 0;
}

.hour-temp {
    font-size: 0.92rem;
    font-weight: 700;
}

.forecast-list {
    padding: 2px 14px 14px;
}

.day-row {
    display: grid;
    grid-template-columns: 52px 26px 38px 30px 1fr 32px;
    align-items: center;
    gap: 10px;
    padding: 9px 8px;
    border-radius: 12px;
    transition: background 0.15s ease;
}

.day-row:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.day-row--today {
    background: rgba(255, 255, 255, 0.08);
}

.day-name {
    display: flex;
    flex-direction: column;
    line-height: 1.1;
}

.day-weekday {
    font-size: 0.85rem;
    font-weight: 600;
}

.day-date {
    font-size: 0.68rem;
    opacity: 0.6;
}

.day-icon {
    color: #fff;
    opacity: 0.95;
}

.day-prob {
    font-size: 0.72rem;
    font-weight: 600;
    color: #8ccbff;
    text-align: center;
}

.day-prob--zero {
    opacity: 0;
}

.day-lo {
    font-size: 0.9rem;
    font-weight: 500;
    text-align: right;
    opacity: 0.65;
}

.day-hi {
    font-size: 0.95rem;
    font-weight: 700;
    text-align: right;
}

.range-track {
    position: relative;
    height: 6px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.18);
}

.range-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    border-radius: 3px;
}
</style>
